import React from 'react';
import { useDispatch } from 'react-redux';
import { MainActions } from '../actions/MainActions';
import { useAuth } from '../contexts/AuthContext';
import ServicesContext from '../contexts/ServicesContext';
import { OrderAlertType } from '../enums/OrderAlertType';
import { OrderType } from '../enums/OrderType';
import { GlobalPickerPerformType } from '../enums/GlobalPickerPerformType';
import { ProfileType } from '../enums/ProfileType';
import { OrderForListing } from '../models/OrderForListing';
import { strings } from '../resources/strings';
import { FilterOrderInput } from '../services/Orders/FilterOrder/FilterOrderInput';
import { FrontVersionResources } from '../models/FrontVersion';

export function useBaseListOrders() {

  const { session, userLocations, signOut } = useAuth();

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [ordersForListing, setOrdersForListing] = React.useState<OrderForListing[]>([]);
  const [selectedAll, setSelectedAll] = React.useState<boolean>(true);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(0);
  const [amountOfPages, setAmountOfPages] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(session?.user?.profileType === ProfileType.Vendor ? 75 : 25);
  const [modalUpdateStateOrderVisible, setModalUpdateStateOrderVisible] = React.useState<boolean>(false);
  const [modalUpdateStatePrescriptionVisible, setModalUpdateStatePrescriptionVisible] = React.useState<boolean>(false);
  const [selectedOrdersForModalUpdateStateOrder, setSelectedOrdersForModalUpdateStateOrder] = React.useState<OrderForListing[] | undefined>(undefined);
  const [selectedOrdersForModalUpdateStatePrescription, setSelectedOrdersForModalUpdateStatePrescription] = React.useState<OrderForListing[] | undefined>(undefined);
  const [filtering, setFiltering] = React.useState<boolean>(true);
  const [modalNotifyCustomersToCollectRecipeAndDNPsVisible, setModalNotifyCustomersToCollectRecipeAndDNPsVisible] = React.useState<boolean>(false);
  const [selectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs, setSelectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs] = React.useState<OrderForListing[] | undefined>(undefined);
  const [modalUpdateStateOrderByGlobalPickerVisible, setModalUpdateStateOrderByGlobalPickerVisible] = React.useState<boolean>(false);
  const [globalPickerPerformTypeForModal, setGlobalPickerPerformTypeForModal] = React.useState<GlobalPickerPerformType | undefined>(undefined);
  const [selectedOrdersForModalUpdateStateOrderByGlobalPicker, setSelectedOrdersForModalUpdateStateOrderByGlobalPicker] = React.useState<OrderForListing[] | undefined>(undefined);

  const {
    filterOrderService,
    toastService,
    orderForListingService
  } = React.useContext(ServicesContext);

  const listHasAnySelectedOrder = React.useMemo(() =>
    ordersForListing.length > 0 && ordersForListing.some(xo => xo._selected)
    , [ordersForListing]);

  function listedOrderCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin(orderForListing: OrderForListing) {
    return orderForListing.order.isCrossChannelSale &&
      orderForListing.order.locationId === 1 &&
      orderForListing.order.originLocationId &&
      orderForListing.order.originLocationId > 1;
  }

  function listedOrderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(orderForListing: OrderForListing) {
    const isCrossChannelSaleFulfilledByEcommerceAndWithPhysicalStoreOrigin =
      listedOrderCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin(orderForListing);

    return session?.user?.profileType == ProfileType.Vendor && isCrossChannelSaleFulfilledByEcommerceAndWithPhysicalStoreOrigin
      ? false
      : true;
  }

  function selectAll() {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    const newOrders = ordersForListing.map(orderForListing => {
      orderForListing._selected = !orderForListing?.order?.isCrossChannelSale || 
        listedOrderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(orderForListing)
          ? newSelectedAll : false;
      return orderForListing;
    });
    setOrdersForListing(newOrders);
  }

  function selectOrder(orderId: number) {
    const newOrders = ordersForListing.map(orderForListing => {
      if (orderForListing.order.id === orderId) {
        orderForListing._selected = !orderForListing._selected;
      }
      return orderForListing;
    });
    setOrdersForListing(newOrders);
    setSelectedAll(newOrders.every(orderForListing => orderForListing._selected));
  }

  function handleFilterOrder(filterInput: FilterOrderInput) {
    mainActions.setNotWait(true);
    setFiltering(true);
    filterInput.orderType = OrderType.Descending;
    filterInput.isOpened = filterInput.number || filterInput.numbers || filterInput.orderBatchId ? null : true;
    filterOrderService.handleForListing(filterInput)
      .then(result => {
        const newOrders = (result?.value?.list || []).map(orderForListing => {
          const canSelect = session?.user?.profileType !== ProfileType.GlobalPicker &&
            (!orderForListing?.order?.isCrossChannelSale || 
             listedOrderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(orderForListing));
          orderForListing._selected = canSelect ? selectedAll : false;
          return orderForListing;
        });
        setOrdersForListing(newOrders);
        setCurrentPage(result?.value?.currentPage || 0);
        setCurrentPageSize(result?.value?.currentPageSize || 0);
        setAmountOfPages(result?.value?.amountOfPages || 0);
        setTotalAmount(result?.value?.totalAmount || 0);

        const countNoCustomerAlerts = newOrders?.reduce((total, order) =>
          order._alerts && order._alerts.length > 0 && order._alerts.some(alt => alt.type === OrderAlertType.NoCustumer)
            ? total + 1
            : total
          , 0) || 0;

        if (countNoCustomerAlerts > 0) {
          if (countNoCustomerAlerts > 1)
            toastService.warning(`Atenção: ${countNoCustomerAlerts} pedidos sem cliente definido!`);
          else
            toastService.warning(`Atenção: um pedido sem cliente definido!`);
        }

        const countIsLateAlerts = newOrders?.reduce((total, order) =>
          order._alerts && order._alerts.length > 0 && order._alerts.some(alt => alt.type === OrderAlertType.OrderIsLate)
            ? total + 1
            : total
          , 0) || 0;

        if (countIsLateAlerts > 0) {
          if (countIsLateAlerts > 1)
            toastService.warning(`Atenção: ${countIsLateAlerts} pedidos com prazo interno vencido!`);
          else
            toastService.warning(`Atenção: um pedido com prazo interno vencido!`);
        }

        const countNoShippingLineAlerts = newOrders?.reduce((total, order) =>
          order._alerts && order._alerts.length > 0 && order._alerts.some(alt => alt.type === OrderAlertType.NoShippingLine)
            ? total + 1
            : total
          , 0) || 0;

        if (countNoShippingLineAlerts > 0) {
          if (countNoShippingLineAlerts > 1)
            toastService.warning(`Atenção: ${countNoShippingLineAlerts} pedidos sem linha de transporte!`);
          else
            toastService.warning(`Atenção: um pedido sem linha de transporte!`);
        }

        const countPaymentPending = newOrders?.reduce((total, order) => 
          order._alerts && order._alerts.length > 0 && order._alerts.some(alt => alt.type === OrderAlertType.PaidPending)
            ? total + 1
            : total
          , 0) || 0;
        
        if (countPaymentPending > 0) {
          if (countPaymentPending > 1)
            toastService.warning(`Atenção: ${countPaymentPending} pedidos com pagamento pendente!`);
          else
            toastService.warning(`Atenção: um pedido com pagamento pendente!`);
        }

        const countExternallyChanged = newOrders?.reduce((total, order) => order._externallyChanged ? total + 1 : total, 0) || 0;
        if (countExternallyChanged > 0) {
          if (countExternallyChanged > 1)
            toastService.warning(`Atenção: ${countExternallyChanged} pedidos com alteração externa!`);
          else
            toastService.warning(`Atenção: um pedido com alteração externa!`);
        }

        const countPreSaleSport = newOrders?.reduce((total, order) => order._preSaleSport ? total + 1 : total, 0) || 0;
        if (countPreSaleSport > 0) {
          if (countPreSaleSport > 1)
            toastService.warning(`Atenção: ${countPreSaleSport} pré-vendas sport!`);
          else
            toastService.warning(`Atenção: uma pré-venda sport!`);
        }
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setFiltering(false);
      });
  }

  async function updateStateOrder() {
    const selectedOrders = orderForListingService.getSelectedOrders(ordersForListing);

    const userConfirmation = await orderForListingService.getUserConfirmationBeforeShowModalToUpdateStateForSelectedOrders(selectedOrders);

    if (!userConfirmation)
      return;

    setModalUpdateStateOrderVisible(true);
    setSelectedOrdersForModalUpdateStateOrder(selectedOrders);
  }

  async function updateStatePrescription() {
    const selectedOrders = orderForListingService.getSelectedOrders(ordersForListing);

    const permission = await orderForListingService.getPermissionBeforeShowModalToUpdateStateForSelectedOrders(selectedOrders);

    if (!permission)
      return;

    setModalUpdateStatePrescriptionVisible(true);
    setSelectedOrdersForModalUpdateStatePrescription(selectedOrders);
  }

  function closeModalUpdateStateOrders() {
    setModalUpdateStateOrderVisible(false);
  }

  function closeModalUpdateStatePrescriptions() {
    setModalUpdateStatePrescriptionVisible(false);
  }

  function closeModalModalNotifyCustomersToCollectRecipeAndDNPs() {
    setModalNotifyCustomersToCollectRecipeAndDNPsVisible(false);
  }

  async function notifyCustomersToCollectRecipeAndDNPs() {
    const selectedOrders = orderForListingService.getSelectedOrders(ordersForListing);

    setModalNotifyCustomersToCollectRecipeAndDNPsVisible(true);
    setSelectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs(selectedOrders);
  }

  async function handleRefreshOrder(orderId?: number) {
    if (!orderId) return;

    const targetOrderForListing = ordersForListing?.find(xo => xo.order.id === orderId);
    if (!targetOrderForListing) return;

    const input = new FilterOrderInput();
    input.orderId = orderId;
    input.frontVersion = FrontVersionResources.currentNumber;
    input.separationType = targetOrderForListing.order.separationType;
    input.userId = session?.user?.id;
    const result = await filterOrderService.handleForListing(input);
    
    const newOrders = (result?.value?.list || []).map(orderForListing => {
      orderForListing._selected = !orderForListing?.order?.isCrossChannelSale ||
        listedOrderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(orderForListing)
          ? targetOrderForListing._selected : false;
      return orderForListing;
    });

    if (newOrders.length > 0) {
      const newOrderForListing = newOrders[0];
      setOrdersForListing(ordersForListing.map(item => item.order.id === orderId ? newOrderForListing : item));
    }
  }

  async function updateStateOrderByGlobalPicker(globalPickerPerformType: GlobalPickerPerformType) {
    const selectedOrders = orderForListingService.getSelectedOrders(ordersForListing);

    const userConfirmation = await orderForListingService.getUserConfirmationBeforeShowModalToUpdateStateByGlobalPickerForSelectedOrders(selectedOrders);

    if (!userConfirmation)
      return;

    setModalUpdateStateOrderByGlobalPickerVisible(true);
    setSelectedOrdersForModalUpdateStateOrderByGlobalPicker(selectedOrders);
    setGlobalPickerPerformTypeForModal(globalPickerPerformType);
  }

  function closeModalUpdateStateOrdersByGlobalPicker() {
    setModalUpdateStateOrderByGlobalPickerVisible(false);
  }

  return {
    session, userLocations, signOut,
    ordersForListing, setOrdersForListing,
    selectedAll, setSelectedAll,
    currentPage, setCurrentPage,
    currentPageSize, setCurrentPageSize,
    amountOfPages, setAmountOfPages,
    totalAmount, setTotalAmount,
    pageSize, setPageSize,
    modalUpdateStateOrderVisible, setModalUpdateStateOrderVisible,
    selectedOrdersForModalUpdateStateOrder, setSelectedOrdersForModalUpdateStateOrder,
    modalUpdateStatePrescriptionVisible, setModalUpdateStatePrescriptionVisible,
    selectedOrdersForModalUpdateStatePrescription, setSelectedOrdersForModalUpdateStatePrescription,
    selectAll,
    selectOrder,
    handleFilterOrder,
    updateStateOrder,
    updateStatePrescription,
    closeModalUpdateStateOrders,
    closeModalUpdateStatePrescriptions,
    listHasAnySelectedOrder,
    filtering,
    notifyCustomersToCollectRecipeAndDNPs,
    modalNotifyCustomersToCollectRecipeAndDNPsVisible, setModalNotifyCustomersToCollectRecipeAndDNPsVisible,
    selectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs, setSelectedOrdersForModalNotifyCustomersToCollectRecipeAndDNPs,
    closeModalModalNotifyCustomersToCollectRecipeAndDNPs,
    handleRefreshOrder,
    updateStateOrderByGlobalPicker,
    modalUpdateStateOrderByGlobalPickerVisible, setModalUpdateStateOrderByGlobalPickerVisible,
    globalPickerPerformTypeForModal,
    selectedOrdersForModalUpdateStateOrderByGlobalPicker,
    closeModalUpdateStateOrdersByGlobalPicker,
    listedOrderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected,
    listedOrderCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin
  };
}